import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from '../../components/ui/notify';
import { validateOTP, validateForm } from '../../utils/validation';
import { verifyRegisterOTPApi, sendActiveAccountOtpFromLoginApi } from '../../services/auth/authService';
import { useOrganizationBranding } from '../../hooks/useOrganizationBranding';
import { AuthOrgBrandingLogo } from '../../components/auth/AuthOrgBrandingLogo';
import { getAuthApiErrorMessage } from './authApiError';

function ActiveAccountPage() {
  const orgBranding = useOrganizationBranding('ActiveAccount');
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const domain = window.location.origin;


  const [formData, setFormData] = useState({
    otp: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);

  // Validation Rules
  const validationRules = {
    otp: validateOTP
  };

  // Handle Change
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Only allow digits and limit to 6 characters
    if (name === 'otp' && !/^\d*$/.test(value)) {
      return;
    }

    // Update form data and clear errors
    const newFormData = { ...formData, [name]: value.slice(0, 6) };
    const newErrors = { ...errors, [name]: undefined };

    setFormData(newFormData);
    setErrors(newErrors);
  };

  const handleResendOTP = async () => {
    if (!email) {
      console.error("[ActiveAccount] No email in state");
      return;
    }
    setIsResending(true);
    const loaderStart = Date.now();
    try {
      const payload = { email, domain };
      const res = await sendActiveAccountOtpFromLoginApi(payload);
      let elapsed = Date.now() - loaderStart;
      if (elapsed < 800) await new Promise(r => setTimeout(r, 800 - elapsed));
      const msg = res?.data?.message ?? res?.message;
      if (msg) toast.success(msg, { position: "top-center", autoClose: 2000 });
    } catch (error) {
      console.error("[ActiveAccount] Resend OTP error:", error?.response?.data ?? error);
      let elapsed = Date.now() - loaderStart;
      if (elapsed < 800) await new Promise(r => setTimeout(r, 800 - elapsed));
      toast.error(getAuthApiErrorMessage(error), { position: "top-center", autoClose: 3000 });
    } finally {
      setIsResending(false);
    }
  };

  // Handle Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validation = validateForm(formData, validationRules);
    if (validation.isValid) {
      setIsLoading(true);
      const loaderStart = Date.now();
      try {
        const payload = { email, domain, otp: formData.otp };
        const response = await verifyRegisterOTPApi(payload);
        let elapsed = Date.now() - loaderStart;
        if (elapsed < 800) await new Promise(r => setTimeout(r, 800 - elapsed));

        if (response.success) {
          const msg = response.data?.message ?? response.message;
          if (msg) toast.success(msg, {
            position: "top-center",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            onClose: () => navigate('/auth/login'),
          });
        } else {
          if (response?.message) toast.error(response.message, { position: "top-center", autoClose: 2000 });
          setIsLoading(false);
        }
      } catch (error) {
        console.error("[ActiveAccount] Verify OTP error:", error?.response?.data ?? error);
        let elapsed = Date.now() - loaderStart;
        if (elapsed < 800) await new Promise(r => setTimeout(r, 800 - elapsed));
        toast.error(getAuthApiErrorMessage(error), {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setIsLoading(false);
      }
    } else {
      setErrors(validation.errors);
    }
  };

  return (
    <div className={"max-[480px]:tw-p-4 [[data-theme='dark']_&]:tw-bg-[#1a1b1e] tw-min-h-[100dvh] tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-[2rem_1rem] tw-bg-[#F5F7FF] [background:radial-gradient(ellipse_at_20%_0%,_#e8edff_0%,_transparent_45%),_#f5f7fb] auth-container"}>
      <div className={"max-[480px]:tw-p-6 [[data-theme='dark']_&]:tw-bg-[#25262b] [[data-theme='dark']_&]:tw-shadow-[0px_4px_20px_rgba(0,_0,_0,_0.15)] tw-relative tw-bg-[#FFFFFF] tw-rounded-[1.25rem] tw-p-10 tw-w-full tw-max-w-[28.75rem] tw-shadow-[0_1rem_3rem_-1.5rem_rgb(30_41_59_/_.25)] tw-border-[1px] tw-border-solid tw-border-[#e2e8f0] max-[767px]:tw-p-[1.75rem_1.5rem] auth-card"}>
        <div className={"tw-text-center tw-mb-8 [&_h2]:tw-text-[#101828] [&_h2]:tw-text-[1.5625rem] [&_h2]:[font-weight:650] [&_h2]:tw-leading-[2rem] [&_h2]:tw-m-[0_0_.5rem_0] [&_h2]:tw-mt-6 [&_h2]:tw-tracking-[-.0375rem] [&_p]:tw-text-[#64748b] [&_p]:tw-text-[.875rem] [&_p]:tw-leading-[1.5rem] [&_p]:tw-m-0 [&_img]:tw-max-h-[4rem] [&_img]:tw-object-contain auth-header"}>
          <AuthOrgBrandingLogo orgBranding={orgBranding} />
          <h2>Activate Your Account</h2>
          <p>Enter the 6-digit code sent to {email ? email : 'your email'}</p>
                </div>

        <form onSubmit={handleSubmit} className={"[&_.ui-button-primary]:tw-mt-[var(--spacing-xs)] [&_.ui-button-primary]:tw-mb-[var(--spacing-xs)] tw-flex tw-flex-col tw-gap-[var(--spacing-md)] auth-form"}>
          <div className={"tw-relative tw-flex tw-flex-col tw-gap-1.5 tw-min-h-[4.5rem] tw-mb-4 [&_label]:tw-text-[#344054] [&_label]:tw-text-[.875rem] [&_label]:tw-font-medium [&_label]:tw-leading-[1.25rem] auth-form-group"}>
            <label htmlFor="otp">Enter OTP</label>
            <div className="tw-relative tw-flex tw-items-center auth-input-wrapper">
              <Icon icon="mdi:key-outline" className={"tw-absolute tw-left-[.875rem] tw-text-[#667085] tw-text-[1.25rem] [[data-theme='dark']_&]:tw-text-[rgba(255,_255,_255,_0.5)] auth-input-icon"} />
              <input
                type="text"
                id="otp"
                name="otp"
                value={formData.otp}
                onChange={handleChange}
                placeholder="Enter 6-digit OTP"
                className={`[&.error:focus]:tw-border-[var(--error-border)] [&.error:focus]:tw-shadow-[0_0_0_4px_rgba(253,_162,_155,_0.1)] [&::placeholder]:tw-text-[#667085] [[data-theme='dark']_&::placeholder]:tw-text-[var(--text-light)] [[data-theme='dark']_&]:[background:rgba(255,_255,_255,_0.05)] [[data-theme='dark']_&]:tw-border-[rgba(255,_255,_255,_0.1)] [[data-theme='dark']_&]:tw-text-[white] [[data-theme='dark']_&:focus]:[background:rgba(255,_255,_255,_0.08)] [[data-theme='dark']_&:focus]:tw-border-[#4361ee] tw-w-full tw-h-[2.75rem] tw-p-[.625rem_.875rem_.625rem_2.625rem] tw-border-[1px] tw-border-solid tw-border-[#dbe1eb] tw-rounded-[.625rem] tw-bg-[#fff] tw-text-[1rem] tw-leading-[1.5rem] tw-text-[#101828] [transition:all_0.15s_ease-in-out] tw-min-h-[2.875rem] [font-family:inherit] [&:focus]:tw-border-[#5265e8] [&:focus]:tw-shadow-[0_0_0_3px_#eef2ff] [&:focus]:[outline:none] [&.error]:tw-border-[#fbbf24] [&.error]:tw-shadow-[0_0_0_4px_rgba(253,_162,_155,_0.1)] [&.error]:tw-bg-[#fffbeb] auth-input   ${errors.otp ? "error" : ""}`}
                disabled={isLoading}
                maxLength={6}
              />
            </div>
            {errors.otp && <span className="tw-text-[#92400e] tw-text-[.75rem] tw-mt-[4px] tw-block auth-error-message">{errors.otp[0]}</span>}
          </div>

          <button
            type="submit"
            className={`[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary   ${isLoading ? "loading" : ""}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="tw-w-[20px] tw-h-[20px] tw-border-[2px] tw-border-solid tw-border-[rgba(255,_255,_255,_0.3)] tw-rounded-[50%] tw-border-t-[#fff] tw-animate-[spin_0.8s_linear_infinite] tw-mr-[8px] tw-inline-block spinner"></div>
                Activating Account...
              </>
            ) : (
              'Activate Account'
            )}
          </button>

          <div className={"tw-mt-[var(--spacing-xs)] tw-text-center tw-text-[#667085] tw-text-[.875rem] tw-leading-[1.25rem] [&_a]:tw-text-[var(--primary-color)] [&_a]:tw-no-underline [&_a]:tw-font-medium [[data-theme='dark']_&]:tw-text-[rgba(255,_255,_255,_0.6)] [[data-theme='dark']_&_a]:tw-text-[#4895ef] auth-options"}>
            <div style={{ marginBottom: '10px' }}>
              Didn't receive the code?{' '}
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={isLoading || isResending}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3152e8',
                  cursor: isLoading || isResending ? 'not-allowed' : 'pointer',
                  opacity: isLoading || isResending ? 0.6 : 1
                }}
              >
                {isResending ? 'Sending...' : 'Resend OTP'}
              </button>
            </div>
            <span>Back to </span>
            <Link to="/auth/login" className={isLoading ? "tw-opacity-[0.6] tw-pointer-events-none disabled-link" : ""}>Login</Link>
        </div>
        </form>
      </div>
    </div>
  );
}

export default ActiveAccountPage;